// @ts-nocheck
/* eslint-disable no-underscore-dangle */
import React, { useEffect, useState } from 'react'
import { noop, delay, defaults, forEach, keyBy, map, isEqual, isEmpty, uniqueId } from 'lodash'
import { useFormik } from 'formik'
import * as PropTypes from 'prop-types'
import * as Yup from 'yup'
// Data
import { useI18n } from '@ggs/i18n'
import { Forms } from '../schema'
// Components
import FormField from '../FormField/FormField'
import FormActions from './FormActions'
import './_Form.scss'
import { setFormValues, useDispatch, useSelector } from '@ggs/store'

const propTypes = {
  ...Forms.FormPropTypes,
  autoComplete: PropTypes.bool,
  className: PropTypes.string,
  onSubmit: PropTypes.func,
}

const defaultProps = {
  ...Forms.FormDefaultProps,
  autoComplete: '',
  className: '',
  onSubmit: (formValues, { setSubmitting }) => {
    setTimeout(() => {
      // console.log('Form submit', JSON.stringify(formValues, null, 2))
      setSubmitting(false)
    }, 400)
  },
}

const recursiveFieldData = (field, name, initialValues, formDraftState = {}, validationSchema) => {
  if (!field) {
    return
  }

  const optionalValueFieldTypes = [Forms.FieldTypes.checkbox, Forms.FieldTypes.switch]

  let fieldValue = field.value
  if (optionalValueFieldTypes.includes(field.type)) {
    fieldValue = field.checked === true && field.value ? [field.value] : null
  }

  if (fieldValue || formDraftState[name]) {
    initialValues[name] = fieldValue || formDraftState[name]
    if (optionalValueFieldTypes.includes(field.type)) {
      field.checked = true
    }
  }

  // If we have a group of validators, parse them.
  if (Array.isArray(field.validators)) {
    field.validators.forEach(({ name: fieldName, validator }) => {
      validationSchema[fieldName] = validator
    })
  }
  // If the field has a dedicated validator
  if (field?.validator) {
    validationSchema[name] = field.validator
  }

  // Ensure to include subfields if present.
  if (field?.subfields) {
    // console.log('::recursiveFieldData subfields found')
    forEach(field.subfields, (subfield, subfieldName) =>
      recursiveFieldData(subfield, subfieldName, initialValues, formDraftState, validationSchema)
    )
  }
}

const obtainFieldData = (fields, formDraftState) => {
  // console.log('rebuilding form field data')
  const initialValues = {}
  const validationSchema = {}
  forEach(fields, (field, name) =>
    recursiveFieldData(field, name, initialValues, formDraftState, validationSchema)
  )
  // console.log('::obtainFieldData results', initialValues, validationSchema)
  return { initialValues, validationSchema }
}

const useForm = (
  {
    fields = [],
    formId = uniqueId('form_'),
    withDraftState = false,
    className = '',
    actions = [],
    content = {},
    onSubmit = noop,
    autoComplete,
    ...props
  },
  formikProps = null
) => {
  const dispatch = useDispatch()
  const { t } = useI18n()
  // const dispatch = useDispatch()
  const formsDraftState = useSelector((state) => state.forms)
  const [formActions, setFormActions] = useState(actions)
  const [state, setState] = useState(() => {
    const settledFields = Array.isArray(fields) ? keyBy(fields, 'name') : fields
    return {
      fields: settledFields,
      content,
      ...obtainFieldData(settledFields, withDraftState ? formsDraftState[formId] || {} : {}),
    }
  })
  const { header, middle, footer } = state.content || {}
  const formik = useFormik({
    initialValues: state.initialValues,
    validationSchema: Yup.object().shape(state.validationSchema),
    onSubmit: (values) => {
      delay(
        () =>
          onSubmit(
            values,
            formik,
            (message) => {}
            // dispatch(addNotification(uniqueId('formik_form_submit'), 'success', message))
          ),
        500
      )
    },
    ...formikProps,
  })

  const setValidators = (updateCallback) => {
    const revised = updateCallback(state.validationSchema)
    setState((old) =>
      Object.assign(old, {
        validationSchema: {
          ...old.validationSchema,
          ...revised,
        },
      })
    )
    // console.log('Form::setValidators', revised)
  }

  /**
   * State supporting checks.
   */
  // Fields can only be updated post load, never replace the original fields to reduce effort.
  const setFields = (newFields) => {
    if (!isEqual(newFields, state.fields)) {
      const newState = defaults({ fields: newFields, ...obtainFieldData(newFields) }, state)
      setState(newState)
    }
  }

  /**
   * An isolated setter for just a single field to safe guard.
   * @param {string} name
   * @param {any} data
   */
  const setField = (name, data) => {
    setFields({ ...fields, [name]: Object.assign(fields[name], data) })
    // console.log('useForm::setField', name, data)
  }

  // Actions can only be updated post load, never replace the original fields to reduce effort.
  const setActions = (newActions) => {
    if (!isEqual(newActions, formActions)) {
      setFormActions(newActions)
    }
  }

  // Refresh content changes
  useEffect(() => {
    if (!isEqual(content, state.content)) {
      setState({ ...state, content })
    }
  }, [content])

  // If we have a watcher for the formContext, trigger it as state changes.
  useEffect(() => {
    // Only ever trigger the fallback error (invalid fields) if there's no pressing API/user error.
    if (formik.isSubmitting && formik.submitCount > 0 && !formik.isValid) {
      //dispatch(addNotification(uniqueId('formik_form_error'), 'error', t('global:form.error.invalidSubmission')))
      console.log('FormErrorRaised:', formik.errors)
    }
  }, [formik.isSubmitting, formik.submitCount, formik.isValid])

  useEffect(() => {
    // When draft state is enabled and formik has input values, persist data in Redux.
    if (withDraftState && !isEmpty(formik.values)) {
      dispatch(
        setFormValues({
          formId,
          values: formik.values,
        })
      )
    }
  }, [formik.values])

  // Start formik state to initial values when formik values is empty.
  useEffect(() => {
    if (isEmpty(formik.values) && state?.initialValues) {
      formik.setValues(state.initialValues)
    }
  }, [state?.initialValues])

  const FormComp = (
    <div>
      <form
        className={`${className} form`}
        // Determine whether this form should allow auto-completion of fields.
        autoComplete={autoComplete ? 'on' : 'off'}
      >
        <div className="form__content">
          {header && <header className="form__header">{header}</header>}
          <div className="form__fields">
            {map(state.fields, (field, name) => {
              // console.log("FormFieldFieldDef:", field)
              const extraProps = { ...field }
              return (
                <FormField
                  key={`formInput_${name}`}
                  name={name}
                  formik={formik}
                  // onChange={(event) => valueChanged(name, event, updateForm)}
                  setField={(data) => setField(name, data)}
                  setValidators={setValidators}
                  {...extraProps}
                />
              )
            })}
          </div>
          {middle && <section className="form__middle">{middle}</section>}
        </div>
        <div className="form__actions">
          <FormActions actions={formActions} formik={formik} />
        </div>
        {footer && <footer className="form__footer">{footer}</footer>}
      </form>
    </div>
  )

  // console.log('render', state, { values: formik.values, errors: formik.errors })
  // console.log('Form::render', { values: formik.values, errors: formik.errors })
  //console.log('render', state, formik)
  // eslint-disable-next-line no-debugger
  // debugger

  return { context: formik, fields: state.fields, setFields, setActions, FormComp }
}

useForm.defaultProps = defaultProps
useForm.propTypes = propTypes
export default useForm
